import React, { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Select, Empty, Badge, Modal } from "@/components/ui-kit";
import { Save, BarChart3, ClipboardList, CheckCircle2, FileText, Sheet, Award, ChevronRight, X, CalendarClock, Check, Minus, LayoutGrid } from "lucide-react";
import { fmtDate } from "@/lib/utils";
import { toast } from "sonner";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";

const STATUS_HEX = { present: "#22c55e", excuse: "#3b82f6", indisponible: "#6b7280", malade: "#eab308", blesse: "#f97316", non_prevenu: "#ef4444" };

export const STATUSES = [
  { key: "present", label: "Présent", color: "green" },
  { key: "excuse", label: "Excusé", color: "blue" },
  { key: "indisponible", label: "Indisponible", color: "gray" },
  { key: "malade", label: "Malade", color: "yellow" },
  { key: "blesse", label: "Blessé", color: "red" },
  { key: "non_prevenu", label: "Pas prévenu", color: "red" },
];
const LABEL = Object.fromEntries(STATUSES.map((s) => [s.key, s.label]));

export default function Attendance() {
  const { can, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const canExport = can("data_export");
  const canRecord = can("attendance_manage");
  const [view, setView] = useState("sheet");
  useEffect(() => { if (!canRecord) setView("stats"); }, [canRecord]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [eventId, setEventId] = useState("");
  const [records, setRecords] = useState({});
  const [declared, setDeclared] = useState({});
  const [confirmed, setConfirmed] = useState({});
  const [stats, setStats] = useState({});
  const [statsCat, setStatsCat] = useState("");
  const [statsGroup, setStatsGroup] = useState("");
  const [coaches, setCoaches] = useState([]);
  const [coachPres, setCoachPres] = useState({});
  const [seasons, setSeasons] = useState([]);
  const [season, setSeason] = useState("");
  const [memberStatus, setMemberStatus] = useState("active");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [coachStats, setCoachStats] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [coachDetail, setCoachDetail] = useState(null);
  const [avMonth, setAvMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [avData, setAvData] = useState({ coaches: [], rows: [] });

  const openCoachDetail = async (coach) => {
    setCoachDetail({ coach, sessions: [], loading: true });
    try {
      const { data } = await api.get(`/attendance/coach-detail/${coach.coach_id}`, { params: periodParams() });
      setCoachDetail({ coach, sessions: data.sessions || [], loading: false });
    } catch { setCoachDetail({ coach, sessions: [], loading: false }); }
  };

  const correctCoach = async (coach, session, present) => {
    try {
      if (present === null) await api.delete(`/events/${session.event_id}/coach-attendance/${coach.coach_id}`);
      else await api.post(`/events/${session.event_id}/coach-attendance`, { records: [{ coach_user_id: coach.coach_id, present }] });
      setCoachDetail((cd) => ({ ...cd, sessions: cd.sessions.map((s) => s.event_id === session.event_id ? { ...s, present } : s) }));
      loadStats();
      toast.success("Présence corrigée");
    } catch { toast.error("Correction échouée"); }
  };

  const loadAvailability = () => {
    api.get("/attendance/coaches/availability-overview", { params: { month: avMonth } }).then((r) => setAvData(r.data)).catch(() => {});
  };
  const setCoachAvail = async (eventId, coachId, status) => {
    try {
      await api.post(`/events/${eventId}/availability/${coachId}/set`, { status });
      loadAvailability();
    } catch { toast.error("Modification échouée"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (view === "coach-avail") loadAvailability(); }, [view, avMonth]);

  const periodParams = () => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    if (!from && !to && season) p.season = season;
    return p;
  };

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data));
    api.get(`/members?status=${memberStatus}`).then((r) => setMembers(r.data));
    api.get("/users?role=coach").then((r) => setCoaches(r.data)).catch(() => {});
    api.get("/attendance/stats/seasons").then((r) => { setSeasons(r.data.seasons || []); setSeason(r.data.current || ""); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberStatus]);

  const loadStats = () => {
    const p = periodParams();
    api.get("/attendance/stats/all", { params: p }).then((r) => setStats(r.data));
    if (canRecord) api.get("/attendance/stats/coaches", { params: p }).then((r) => setCoachStats(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStats(); }, [season, from, to]);

  const openDetail = async (m) => {
    setDetail({ member: { id: m.id, name: `${m.first_name} ${m.last_name}` }, history: [], summary: {} });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/attendance/stats/member/${m.id}`, { params: periodParams() });
      setDetail(data);
    } catch { toast.error("Erreur chargement du détail"); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  useEffect(() => {
    if (!eventId) return;
    api.get(`/attendance/${eventId}`).then((r) => {
      const map = {}, decl = {}, conf = {};
      r.data.forEach((rec) => {
        if (rec.status) map[rec.member_id] = rec.status;
        if (rec.parent_declared) decl[rec.member_id] = rec.parent_declared;
        if (rec.coach_confirmed) conf[rec.member_id] = true;
      });
      setRecords(map); setDeclared(decl); setConfirmed(conf);
    });
    api.get(`/events/${eventId}/coach-attendance`).then((r) => setCoachPres(r.data || {})).catch(() => setCoachPres({}));
  }, [eventId]);

  const selectedEvent = events.find((e) => e.id === eventId);
  const memberCats = [...new Set(members.map((m) => m.category).filter(Boolean))].sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  const autoCats = selectedEvent?.category && members.some((m) => m.category === selectedEvent.category) ? [selectedEvent.category] : [];
  const [sheetCats, setSheetCats] = useState(null); // null = auto ; [] = tous ; [..] = catégories choisies
  const [sheetGroup, setSheetGroup] = useState("all");
  useEffect(() => { setSheetCats(null); }, [eventId]);
  const effectiveCats = sheetCats === null ? autoCats : sheetCats;
  const eventGr = selectedEvent?.group;
  let relevant = effectiveCats.length ? members.filter((m) => effectiveCats.includes(m.category)) : members;
  if (eventGr === "gr1" || eventGr === "gr2") relevant = relevant.filter((m) => m.group === eventGr);
  if (sheetGroup === "gr1" || sheetGroup === "gr2") relevant = relevant.filter((m) => m.group === sheetGroup);
  else if (sheetGroup === "none") relevant = relevant.filter((m) => !m.group);
  const toggleCat = (c) => setSheetCats((prev) => {
    const base = prev === null ? autoCats : prev;
    return base.includes(c) ? base.filter((x) => x !== c) : [...base, c];
  });
  const setStatus = (mid, status) => setRecords({ ...records, [mid]: status });

  const save = async () => {
    const payload = { event_id: eventId, records: Object.entries(records).map(([member_id, status]) => ({ member_id, status })) };
    await api.post("/attendance", payload);
    await api.post(`/events/${eventId}/coach-attendance`, {
      records: coaches.map((c) => ({ coach_user_id: c.id, present: !!coachPres[c.id] })),
    });
    toast.success("Présences confirmées");
    loadStats();
    const r = await api.get(`/attendance/${eventId}`);
    const conf = {}, decl = {}; r.data.forEach((rec) => { if (rec.coach_confirmed) conf[rec.member_id] = true; if (rec.parent_declared) decl[rec.member_id] = rec.parent_declared; });
    setConfirmed(conf); setDeclared(decl);
  };
  const toggleCoach = (cid) => setCoachPres((p) => ({ ...p, [cid]: !p[cid] }));

  const counts = STATUSES.map((s) => Object.values(records).filter((v) => v === s.key).length);

  return (
    <div>
      <PageHeader title="Présence" subtitle={canRecord ? "Feuille de présence et statistiques par joueur." : "Suivi des présences de vos affiliés."} />

      <div className="flex gap-2 mb-6 flex-wrap">
        {canRecord && <TabBtn active={view === "sheet"} onClick={() => setView("sheet")} icon={ClipboardList} label="Feuille de présence" testid="tab-sheet" />}
        <TabBtn active={view === "stats"} onClick={() => setView("stats")} icon={BarChart3} label="Statistiques" testid="tab-stats" />
        {canRecord && <TabBtn active={view === "matrix"} onClick={() => setView("matrix")} icon={LayoutGrid} label="Tableau mensuel" testid="tab-matrix" />}
        {canRecord && <TabBtn active={view === "coach-avail"} onClick={() => setView("coach-avail")} icon={CalendarClock} label="Disponibilités des coachs" testid="tab-coach-avail" />}
      </div>

      {view === "sheet" && canRecord ? (
        <>
          <Card className="mb-6">
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Choisir un événement</label>
            <Select data-testid="attendance-event-select" value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">Sélectionner...</option>
              {events.map((e) => <option key={e.id} value={e.id}>{fmtDate(e.date)} · {e.title}{e.category ? ` (${e.category})` : ""}</option>)}
            </Select>
            {eventId && (
              <div className="mt-4">
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Joueurs à afficher <span className="normal-case font-normal">(séances mixtes : cochez plusieurs catégories)</span></label>
                <div className="flex flex-wrap gap-2" data-testid="attendance-category-chips">
                  <button type="button" data-testid="cat-chip-all" onClick={() => setSheetCats([])}
                    className={`h-8 px-3 rounded-full text-xs font-bold transition-colors ${effectiveCats.length === 0 ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                    Tous les joueurs
                  </button>
                  {memberCats.map((c) => (
                    <button key={c} type="button" data-testid={`cat-chip-${c}`} onClick={() => toggleCat(c)}
                      className={`h-8 px-3 rounded-full text-xs font-bold transition-colors ${effectiveCats.includes(c) ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      {c}
                    </button>
                  ))}
                </div>
                {selectedEvent?.category && !members.some((m) => m.category === selectedEvent.category) && (
                  <p className="text-xs text-yellow-500 mt-2" data-testid="no-category-match">Aucun joueur dans la catégorie « {selectedEvent.category} » de cette séance — tous les joueurs sont affichés.</p>
                )}
                <div className="mt-3">
                  <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Groupe</label>
                  <div className="flex flex-wrap gap-2" data-testid="attendance-group-chips">
                    {[["all", "Tous"], ["gr1", "GR 1"], ["gr2", "GR 2"], ["none", "Sans groupe"]].map(([k, lbl]) => (
                      <button key={k} type="button" data-testid={`sheet-gr-${k}`} onClick={() => setSheetGroup(k)}
                        className={`h-8 px-3 rounded-full text-xs font-bold transition-colors ${sheetGroup === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>

          {eventId ? (
            <>
              <div className="flex gap-2 mb-4 flex-wrap items-center">
                {STATUSES.map((s, i) => counts[i] > 0 && <Badge key={s.key} color={s.color}>{s.label}: {counts[i]}</Badge>)}
                <div className="ml-auto flex gap-2">
                  {canExport && <Button data-testid="export-attendance-pdf" variant="outline" onClick={() => downloadFile(`/export/attendance/${eventId}.pdf`, "presence.pdf").catch(() => toast.error("Export échoué"))}><FileText size={15} /> PDF</Button>}
                  {canExport && <Button data-testid="export-attendance-xlsx" variant="outline" onClick={() => downloadFile(`/export/attendance/${eventId}.xlsx`, "presence.xlsx").catch(() => toast.error("Export échoué"))}><Sheet size={15} /> Excel</Button>}
                </div>
              </div>
              {relevant.length ? (
                <div className="space-y-2">
                  {relevant.map((m) => (
                    <Card key={m.id} data-testid="attendance-row" className="flex items-center justify-between py-3 gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-black shrink-0">{m.first_name?.[0]}{m.last_name?.[0]}</div>
                        <div className="min-w-0">
                          <span className="font-semibold text-sm truncate block">{m.first_name} {m.last_name}</span>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {declared[m.id]
                              ? <span className={`text-[11px] font-bold ${declared[m.id] === "present" ? "text-primary" : "text-destructive"}`}>Parent : {declared[m.id] === "present" ? "sera présent" : "absent"}</span>
                              : <span className="text-[11px] text-muted-foreground">Parent : pas de réponse</span>}
                            {confirmed[m.id] && <span className="text-[11px] text-primary flex items-center gap-0.5"><CheckCircle2 size={11} /> confirmé</span>}
                          </div>
                        </div>
                      </div>
                      <select data-testid={`att-select-${m.id}`} value={records[m.id] || ""} onChange={(e) => setStatus(m.id, e.target.value)}
                        className="h-9 px-3 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-primary shrink-0">
                        <option value="">—</option>
                        {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </Card>
                  ))}
                  {coaches.length > 0 && (
                    <div className="mt-6" data-testid="coach-presence-section">
                      <h3 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground mb-3">Présence des coachs</h3>
                      <div className="space-y-2">
                        {coaches.map((c) => (
                          <Card key={c.id} data-testid="coach-attendance-row" className="flex items-center justify-between py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-sky-500/15 flex items-center justify-center text-sky-400 text-xs font-black shrink-0">{c.name?.split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
                              <span className="font-semibold text-sm">{c.name}</span>
                            </div>
                            <button data-testid={`coach-present-${c.id}`} onClick={() => toggleCoach(c.id)}
                              className={`flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-bold transition-colors ${coachPres[c.id] ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                              <CheckCircle2 size={15} /> {coachPres[c.id] ? "Présent" : "Absent"}
                            </button>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button data-testid="save-attendance-btn" onClick={save} className="mt-4"><Save size={16} /> Enregistrer les présences</Button>
                </div>
              ) : <Empty text="Aucun membre pour cette catégorie." />}
            </>
          ) : <Empty text="Sélectionnez un événement pour marquer les présences." />}
        </>
      ) : view === "stats" ? (
        <>
          {/* Filtres de période */}
          <Card className="mb-6" data-testid="stats-filters">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-44">
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Saison</label>
                <Select data-testid="stats-season-filter" value={season} onChange={(e) => { setSeason(e.target.value); setFrom(""); setTo(""); }} disabled={!!(from || to)}>
                  <option value="">Toutes périodes</option>
                  {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Du</label>
                <input data-testid="stats-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                  className="h-9 px-3 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Au</label>
                <input data-testid="stats-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
                  className="h-9 px-3 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-primary" />
              </div>
              {(from || to) && <Button data-testid="stats-clear-range" variant="outline" onClick={() => { setFrom(""); setTo(""); }}><X size={15} /> Réinitialiser dates</Button>}
              <div className="w-48">
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Catégorie</label>
                <Select data-testid="stats-category-filter" value={statsCat} onChange={(e) => setStatsCat(e.target.value)}>
                  <option value="">Toutes les catégories</option>
                  {[...new Set(members.map((m) => m.category).filter(Boolean))].sort((a, b) => b.localeCompare(a, undefined, { numeric: true })).map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </div>
              <div className="w-40">
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Groupe</label>
                <Select data-testid="stats-group-filter" value={statsGroup} onChange={(e) => setStatsGroup(e.target.value)}>
                  <option value="">Tous les groupes</option>
                  <option value="gr1">GR 1</option>
                  <option value="gr2">GR 2</option>
                </Select>
              </div>
              {canRecord && (
                <div className="w-48">
                  <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Statut affilié</label>
                  <Select data-testid="stats-status-filter" value={memberStatus} onChange={(e) => setMemberStatus(e.target.value)}>
                    <option value="active">Tous (actifs)</option>
                    <option value="season">Affiliés 2026/2027</option>
                    <option value="archived">Archivés</option>
                  </Select>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                {canExport && <Button data-testid="export-stats-pdf" variant="outline" onClick={() => downloadFile("/export/attendance-stats.pdf", "statistiques_presence.pdf").catch(() => toast.error("Export échoué"))}><FileText size={15} /> PDF</Button>}
                {canExport && <Button data-testid="export-stats-xlsx" variant="outline" onClick={() => downloadFile("/export/attendance-stats.xlsx", "statistiques_presence.xlsx").catch(() => toast.error("Export échoué"))}><Sheet size={15} /> Excel</Button>}
              </div>
            </div>
          </Card>

          {/* Graphiques */}
          {canRecord && <StatsCharts members={members.filter((m) => (!statsGroup || m.group === statsGroup))} stats={stats} />}

          {/* Tableau joueurs */}
          <h3 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground mb-3 mt-8">Détail par joueur</h3>
          <StatsTable members={members.filter((m) => (!statsCat || m.category === statsCat) && (!statsGroup || m.group === statsGroup))} stats={stats} onSelect={openDetail} />

          {/* Stats coachs */}
          {canRecord && <>
            <h3 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground mb-3 mt-8 flex items-center gap-2"><Award size={16} className="text-primary" /> Présence des coachs</h3>
            <CoachStatsTable rows={coachStats} onSelect={openCoachDetail} />
          </>}
        </>
      ) : view === "matrix" ? (
        <MatrixView />
      ) : (
        <CoachAvailabilityView month={avMonth} setMonth={setAvMonth} data={avData} editable={isAdmin} onSet={setCoachAvail} />
      )}

      {coachDetail && (
        <Modal open={!!coachDetail} onClose={() => setCoachDetail(null)} title={`Présences — ${coachDetail.coach?.name || ""}`} testid="coach-detail-modal">
          <CoachDetailCorrect detail={coachDetail} onCorrect={(s, p) => correctCoach(coachDetail.coach, s, p)} />
        </Modal>
      )}

      {detail && (
        <Modal open={!!detail} onClose={() => setDetail(null)} title={`Présence — ${detail.member?.name || ""}`} testid="player-detail-modal">
          <PlayerDetail detail={detail} loading={detailLoading} />
        </Modal>
      )}
    </div>
  );
}

const StatsCharts = ({ members, stats }) => {
  const overall = STATUSES.map((s) => ({
    key: s.key, name: s.label,
    value: members.reduce((acc, m) => acc + ((stats[m.id] || {})[s.key] || 0), 0),
  })).filter((d) => d.value > 0);
  const hasData = overall.length > 0;
  const cats = [...new Set(members.map((m) => m.category).filter(Boolean))]
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  const perCat = cats.map((c) => {
    const ms = members.filter((m) => m.category === c);
    let present = 0, total = 0;
    ms.forEach((m) => { const s = stats[m.id] || {}; present += s.present || 0; total += s.total || 0; });
    return { category: c, rate: total ? Math.round((present / total) * 100) : 0, total };
  }).filter((d) => d.total > 0);

  if (!hasData) return <Empty text="Aucune présence enregistrée pour cette période." />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="stats-charts">
      <Card>
        <p className="font-semibold text-sm mb-4">Répartition des statuts (club)</p>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={overall} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
              {overall.map((d) => <Cell key={d.key} fill={STATUS_HEX[d.key] || "#64748b"} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-2 justify-center mt-2">
          {overall.map((d) => <span key={d.key} className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_HEX[d.key] }} /> {d.name} ({d.value})</span>)}
        </div>
      </Card>
      <Card>
        <p className="font-semibold text-sm mb-4">Taux de présence par catégorie</p>
        {perCat.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={perCat} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="category" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} unit="%" />
              <Tooltip cursor={{ fill: "rgba(148,163,184,0.08)" }} contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v}%`, "Taux"]} />
              <Bar dataKey="rate" radius={[6, 6, 0, 0]}>
                {perCat.map((d) => <Cell key={d.category} fill={d.rate >= 75 ? "#22c55e" : d.rate >= 50 ? "#eab308" : "#ef4444"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty text="Aucune catégorie avec données." />}
      </Card>
    </div>
  );
};

const StatsTable = ({ members, stats, onSelect }) => {
  if (!members.length) return <Empty text="Aucun membre." />;
  return (
    <Card className="overflow-x-auto p-0">
      <table className="w-full text-sm hidden md:table" data-testid="stats-table">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground">Joueur</th>
            {STATUSES.map((s) => <th key={s.key} className="px-3 py-3 text-xs uppercase tracking-wider text-muted-foreground text-center">{s.label}</th>)}
            <th className="px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground text-center">Taux présence</th>
            <th className="px-2 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const s = stats[m.id] || {};
            const total = s.total || 0;
            const rate = total ? Math.round(((s.present || 0) / total) * 100) : 0;
            return (
              <tr key={m.id} data-testid="stats-row" onClick={() => onSelect(m)}
                className="border-b border-border/50 hover:bg-secondary/40 cursor-pointer">
                <td className="px-4 py-3 font-semibold whitespace-nowrap">{m.first_name} {m.last_name}</td>
                {STATUSES.map((st) => <td key={st.key} className="px-3 py-3 text-center text-muted-foreground">{s[st.key] || 0}</td>)}
                <td className="px-4 py-3 text-center">
                  <span className={`font-bold ${rate >= 75 ? "text-primary" : rate >= 50 ? "text-yellow-500" : "text-destructive"}`}>{total ? `${rate}%` : "—"}</span>
                </td>
                <td className="px-2 py-3 text-muted-foreground"><ChevronRight size={16} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-border/60">
        {members.map((m) => {
          const s = stats[m.id] || {};
          const total = s.total || 0;
          const rate = total ? Math.round(((s.present || 0) / total) * 100) : 0;
          return (
            <button type="button" key={m.id} data-testid="stats-card" onClick={() => onSelect(m)}
              className="w-full text-left p-4 flex items-center justify-between gap-3 active:bg-secondary/40">
              <div className="min-w-0">
                <p className="font-semibold truncate">{m.first_name} {m.last_name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {STATUSES.map((st) => `${st.label}: ${s[st.key] || 0}`).join(" · ")}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`font-display text-2xl font-black ${rate >= 75 ? "text-primary" : rate >= 50 ? "text-yellow-500" : "text-destructive"}`}>{total ? `${rate}%` : "—"}</span>
                <ChevronRight size={18} className="text-muted-foreground" />
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
};

const CoachStatsTable = ({ rows, onSelect }) => {
  if (!rows.length) return <Empty text="Aucun coach." />;
  const cellBtn = "w-full h-full py-1 rounded-md hover:bg-secondary transition-colors cursor-pointer font-semibold";
  return (
    <Card className="overflow-x-auto p-0">
      <table className="w-full text-sm hidden md:table" data-testid="coach-stats-table">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground">Coach</th>
            <th className="px-3 py-3 text-xs uppercase tracking-wider text-muted-foreground text-center">Présent</th>
            <th className="px-3 py-3 text-xs uppercase tracking-wider text-muted-foreground text-center">Absent</th>
            <th className="px-3 py-3 text-xs uppercase tracking-wider text-muted-foreground text-center">Séances</th>
            <th className="px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground text-center">Taux présence</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.coach_id} data-testid="coach-stats-row" className="border-b border-border/50 hover:bg-secondary/40">
              <td className="px-4 py-3 font-semibold whitespace-nowrap">{c.name}</td>
              <td className="px-3 py-1 text-center text-primary">
                <button data-testid={`coach-present-${c.coach_id}`} onClick={() => onSelect(c)} className={cellBtn} title="Voir le détail et corriger">{c.present}</button>
              </td>
              <td className="px-3 py-1 text-center text-destructive">
                <button data-testid={`coach-absent-${c.coach_id}`} onClick={() => onSelect(c)} className={cellBtn} title="Voir le détail et corriger">{c.absent}</button>
              </td>
              <td className="px-3 py-1 text-center text-muted-foreground">
                <button data-testid={`coach-total-${c.coach_id}`} onClick={() => onSelect(c)} className={cellBtn} title="Voir le détail et corriger">{c.total}</button>
              </td>
              <td className="px-4 py-3 text-center">
                <span className={`font-bold ${c.rate >= 75 ? "text-primary" : c.rate >= 50 ? "text-yellow-500" : "text-destructive"}`}>{c.total ? `${c.rate}%` : "—"}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-border/60">
        {rows.map((c) => (
          <button type="button" key={c.coach_id} data-testid="coach-stats-card" onClick={() => onSelect(c)}
            className="w-full text-left p-4 flex items-center justify-between gap-3 active:bg-secondary/40">
            <div className="min-w-0">
              <p className="font-semibold truncate">{c.name}</p>
              <p className="text-xs text-muted-foreground mt-1">Présent: {c.present} · Absent: {c.absent} · Séances: {c.total}</p>
            </div>
            <span className={`font-display text-2xl font-black shrink-0 ${c.rate >= 75 ? "text-primary" : c.rate >= 50 ? "text-yellow-500" : "text-destructive"}`}>{c.total ? `${c.rate}%` : "—"}</span>
          </button>
        ))}
      </div>
    </Card>
  );
};

const CoachDetailCorrect = ({ detail, onCorrect }) => {
  const { sessions = [], loading } = detail || {};
  if (loading) return <p className="text-sm text-muted-foreground py-6 text-center">Chargement...</p>;
  if (!sessions.length) return <Empty text="Aucune séance sur cette période." />;
  const present = sessions.filter((s) => s.present === true).length;
  const absent = sessions.filter((s) => s.present === false).length;
  const counted = present + absent;
  const Opt = ({ active, color, onClick, children, testid }) => (
    <button data-testid={testid} onClick={onClick}
      className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors ${active ? color : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{children}</button>
  );
  return (
    <div className="space-y-4" data-testid="coach-detail-content">
      <div className="flex flex-wrap gap-2">
        <Badge color="green">Présent : {present}</Badge>
        <Badge color="red">Absent : {absent}</Badge>
        <Badge color="primary">Séances comptées : {counted}</Badge>
      </div>
      <p className="text-xs text-muted-foreground">Cliquez pour corriger le statut de chaque séance. « Non compté » retire la séance du total.</p>
      <div className="space-y-1.5 max-h-80 overflow-y-auto">
        {sessions.map((s) => (
          <div key={s.event_id} data-testid="coach-detail-row" className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-secondary/40">
            <div className="min-w-0">
              <span className="text-sm font-semibold">{fmtDate(s.date)}</span>
              <span className="text-xs text-muted-foreground ml-2 truncate">{s.title}{s.category ? ` · ${s.category}` : ""}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Opt testid={`coach-set-present-${s.event_id}`} active={s.present === true} color="bg-primary text-primary-foreground" onClick={() => onCorrect(s, true)}><Check size={13} /></Opt>
              <Opt testid={`coach-set-absent-${s.event_id}`} active={s.present === false} color="bg-destructive text-white" onClick={() => onCorrect(s, false)}><X size={13} /></Opt>
              <Opt testid={`coach-set-none-${s.event_id}`} active={s.present === null} color="bg-muted text-foreground" onClick={() => onCorrect(s, null)}><Minus size={13} /></Opt>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AVAIL_META = {
  confirmed: { label: "Retenu", cls: "bg-primary text-primary-foreground" },
  available: { label: "Dispo", cls: "bg-primary/15 text-primary" },
  unavailable: { label: "Indispo", cls: "bg-destructive/15 text-destructive" },
  rejected: { label: "Non retenu", cls: "bg-muted text-muted-foreground" },
};

const AVAIL_OPTIONS = [
  { status: "available", label: "Disponible" },
  { status: "unavailable", label: "Indisponible" },
  { status: "confirmed", label: "Retenu" },
  { status: "rejected", label: "Non retenu" },
  { status: null, label: "Effacer" },
];

const CoachAvailabilityView = ({ month, setMonth, data, editable, onSet }) => {
  const { coaches = [], rows = [] } = data || {};
  const [editKey, setEditKey] = useState(null);
  const pick = (eventId, coachId, status) => { setEditKey(null); onSet(eventId, coachId, status); };
  return (
    <div data-testid="coach-avail-view">
      <Card className="mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Mois</label>
            <input data-testid="coach-avail-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="h-9 px-3 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-primary" />
          </div>
          <p className="text-xs text-muted-foreground ml-auto">
            {rows.length} séance(s) · {coaches.length} coach(s){editable ? " · cliquez une case pour modifier" : ""}
          </p>
        </div>
      </Card>
      {!coaches.length ? <Empty text="Aucun coach." /> : !rows.length ? (
        <Empty text="Aucune séance ce mois-ci." />
      ) : (
        <Card className="overflow-x-auto p-0">
          {editKey && <div className="fixed inset-0 z-10" onClick={() => setEditKey(null)} />}
          <table className="w-full text-sm" data-testid="coach-avail-table">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground whitespace-nowrap">Séance</th>
                {coaches.map((c) => <th key={c.id} className="px-3 py-3 text-xs uppercase tracking-wider text-muted-foreground text-center whitespace-nowrap">{c.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const anyAvail = Object.values(r.avail).some((s) => s === "available" || s === "confirmed");
                return (
                <tr key={r.event_id} data-testid="coach-avail-row" className="border-b border-border/50 hover:bg-secondary/40">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${anyAvail ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`} data-testid="session-status">
                        {anyAvail ? "Disponible" : "Indisponible"}
                      </span>
                      <span className="font-semibold">{fmtDate(r.date)}</span>
                      <span className="text-xs text-muted-foreground">{r.title}{r.category ? ` · ${r.category}` : ""}</span>
                    </div>
                  </td>
                  {coaches.map((c) => {
                    const st = r.avail[c.id];
                    const meta = AVAIL_META[st];
                    const k = `${r.event_id}|${c.id}`;
                    const badge = meta
                      ? <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${meta.cls}`}>{meta.label}</span>
                      : <span className="text-muted-foreground/40">—</span>;
                    return (
                      <td key={c.id} className="px-3 py-3 text-center relative">
                        {editable ? (
                          <button data-testid={`avail-cell-${r.event_id}-${c.id}`} onClick={() => setEditKey(editKey === k ? null : k)}
                            className="inline-flex items-center justify-center rounded-lg px-1 py-1 hover:ring-1 ring-primary/40 transition-all">{badge}</button>
                        ) : badge}
                        {editable && editKey === k && (
                          <div data-testid="avail-menu" className="absolute z-20 top-full mt-1 left-1/2 -translate-x-1/2 w-36 rounded-xl border border-border bg-card shadow-2xl overflow-hidden">
                            {AVAIL_OPTIONS.map((o) => (
                              <button key={o.label} data-testid={`avail-opt-${o.status || "clear"}`} onClick={() => pick(r.event_id, c.id, o.status)}
                                className="w-full text-left px-3 py-2 text-xs font-semibold hover:bg-secondary transition-colors">{o.label}</button>
                            ))}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

const PlayerDetail = ({ detail, loading }) => {
  const { history = [], summary = {} } = detail || {};
  if (loading) return <p className="text-sm text-muted-foreground py-6 text-center">Chargement...</p>;
  if (!history.length) return <Empty text="Aucune présence enregistrée sur cette période." />;
  let present = 0;
  const trend = history.map((h, i) => {
    if (h.status === "present") present += 1;
    return { i: i + 1, date: fmtDate(h.date), rate: Math.round((present / (i + 1)) * 100) };
  });
  return (
    <div className="space-y-5" data-testid="player-detail-content">
      <div className="flex flex-wrap gap-2">
        <Badge color="green">Taux : {summary.rate || 0}%</Badge>
        <Badge color="primary">{summary.total || 0} séance(s)</Badge>
        {STATUSES.map((s) => (summary[s.key] ? <Badge key={s.key} color={s.color}>{s.label}: {summary[s.key]}</Badge> : null))}
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Évolution du taux de présence</p>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} unit="%" />
            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v}%`, "Taux cumulé"]} />
            <Line type="monotone" dataKey="rate" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Historique</p>
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {[...history].reverse().map((h) => {
            const st = STATUSES.find((s) => s.key === h.status);
            return (
              <div key={h.event_id} data-testid="player-history-row" className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary/40">
                <div className="min-w-0">
                  <span className="text-sm font-semibold">{fmtDate(h.date)}</span>
                  <span className="text-xs text-muted-foreground ml-2 truncate">{h.title}{h.category ? ` · ${h.category}` : ""}</span>
                </div>
                {st && <Badge color={st.color}>{st.label}</Badge>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const MATRIX_ABBR = {
  present: { l: "P", c: "#22c55e", label: "Présent" },
  excuse: { l: "E", c: "#3b82f6", label: "Excusé" },
  indisponible: { l: "I", c: "#6b7280", label: "Indisponible" },
  malade: { l: "M", c: "#eab308", label: "Malade" },
  blesse: { l: "B", c: "#f97316", label: "Blessé" },
  non_prevenu: { l: "A", c: "#ef4444", label: "Absent (pas prévenu)" },
};
const ADMIN_COLORS = { green: "#22c55e", orange: "#f59e0b", red: "#ef4444", blue: "#3b82f6" };

const MatrixView = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [group, setGroup] = useState("gr1");
  const [data, setData] = useState({ sessions: [], rows: [] });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    setLoading(true);
    api.get("/attendance/matrix", { params: { month, ...(group ? { group } : {}) } })
      .then((r) => setData(r.data)).catch(() => setData({ sessions: [], rows: [] })).finally(() => setLoading(false));
  }, [month, group]);
  const openMenu = (e, mid, eid) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setEditing({ mid, eid, x: rect.left, y: rect.bottom + 4 });
  };
  const setStatus = async (mid, eid, status) => {
    try {
      await api.post("/attendance", { event_id: eid, records: [{ member_id: mid, status }] });
      setData((d) => ({ ...d, rows: d.rows.map((r) => {
        if (r.member_id !== mid) return r;
        const s = { ...r.statuses };
        if (status) s[eid] = status; else delete s[eid];
        return { ...r, statuses: s };
      }) }));
      toast.success("Présence mise à jour");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setEditing(null); }
  };
  const fmtCol = (d) => { const p = (d || "").split("-"); return p.length === 3 ? `${p[2]}/${p[1]}` : d; };
  const { sessions, rows } = data;
  return (
    <div data-testid="matrix-view">
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Mois</label>
            <input data-testid="matrix-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="h-9 px-3 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-primary" />
          </div>
          <div className="w-40">
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Groupe</label>
            <Select data-testid="matrix-group" value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="gr1">GR 1</option>
              <option value="gr2">GR 2</option>
              <option value="">Tous</option>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground ml-auto">{sessions.length} entraînement(s) · {rows.length} joueur(s)</p>
          <Button data-testid="matrix-export-pdf" variant="outline" disabled={!sessions.length || !rows.length}
            onClick={() => downloadFile(`/attendance/matrix/pdf?month=${month}${group ? `&group=${group}` : ""}`, `presences_${month}.pdf`).catch(() => toast.error("Export échoué"))}>
            <FileText size={15} /> Télécharger PDF
          </Button>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          {Object.entries(MATRIX_ABBR).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="w-4 h-4 rounded flex items-center justify-center text-[10px] font-black text-white" style={{ background: v.c }}>{v.l}</span> {v.label}
            </span>
          ))}
        </div>
      </Card>

      {loading ? <p className="text-sm text-muted-foreground py-6 text-center">Chargement...</p>
        : !sessions.length ? <Empty text="Aucun entraînement ce mois-ci pour ce groupe." />
        : !rows.length ? <Empty text="Aucun joueur dans ce groupe." />
        : (
          <Card className="overflow-x-auto p-0">
            <table className="text-sm border-collapse" data-testid="matrix-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="sticky left-0 z-10 bg-card px-4 py-3 text-left text-xs uppercase tracking-widest text-muted-foreground whitespace-nowrap">Joueur</th>
                  <th className="px-3 py-3 text-center text-xs uppercase tracking-wider text-muted-foreground">Année</th>
                  {sessions.map((s) => (
                    <th key={s.event_id} className="px-2 py-3 text-center text-xs font-bold text-muted-foreground whitespace-nowrap" title={s.title}>{fmtCol(s.date)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.member_id} data-testid="matrix-row" className="border-b border-border/50 hover:bg-secondary/30">
                    <td className="sticky left-0 z-10 bg-card px-4 py-2.5 font-semibold whitespace-nowrap" style={r.admin_color ? { color: ADMIN_COLORS[r.admin_color] } : undefined}>{r.last_name} {r.first_name}</td>
                    <td className="px-3 py-2.5 text-center text-muted-foreground">{r.year || "—"}</td>
                    {sessions.map((s) => {
                      const st = r.statuses[s.event_id];
                      const meta = st ? MATRIX_ABBR[st] : null;
                      return (
                        <td key={s.event_id} className="px-2 py-2.5 text-center" data-testid={`matrix-cell-${r.member_id}-${s.event_id}`}>
                          {isAdmin ? (
                            <button data-testid={`matrix-edit-${r.member_id}-${s.event_id}`} onClick={(e) => openMenu(e, r.member_id, s.event_id)}
                              title="Modifier la présence (admin)" className="inline-flex items-center justify-center rounded-md p-0.5 hover:ring-2 hover:ring-primary/50 transition">
                              {meta
                                ? <span className="inline-flex w-6 h-6 rounded items-center justify-center text-[11px] font-black text-white" style={{ background: meta.c }}>{meta.l}</span>
                                : <span className="inline-flex w-6 h-6 rounded items-center justify-center text-muted-foreground/40 border border-dashed border-border text-sm">+</span>}
                            </button>
                          ) : (meta
                            ? <span className="inline-flex w-6 h-6 rounded items-center justify-center text-[11px] font-black text-white" style={{ background: meta.c }} title={meta.label}>{meta.l}</span>
                            : <span className="text-muted-foreground/30">—</span>)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

      {isAdmin && editing && (
        <>
          <div className="fixed inset-0 z-40" data-testid="matrix-menu-backdrop" onClick={() => setEditing(null)} />
          <div className="fixed z-50 bg-card border border-border rounded-lg shadow-xl p-1.5 w-56" data-testid="matrix-status-menu"
            style={{ left: Math.min(editing.x, (typeof window !== "undefined" ? window.innerWidth : 1000) - 236), top: editing.y }}>
            {Object.entries(MATRIX_ABBR).map(([k, v]) => (
              <button key={k} data-testid={`matrix-set-${k}`} onClick={() => setStatus(editing.mid, editing.eid, k)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-secondary text-left text-sm">
                <span className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-black text-white shrink-0" style={{ background: v.c }}>{v.l}</span> {v.label}
              </button>
            ))}
            <button data-testid="matrix-set-clear" onClick={() => setStatus(editing.mid, editing.eid, null)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-secondary text-left text-sm text-muted-foreground border-t border-border mt-1 pt-2">
              <span className="w-5 h-5 rounded flex items-center justify-center text-xs border border-dashed border-border shrink-0">—</span> Effacer
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
    <Icon size={16} /> {label}
  </button>
);
